import SharedDialog from '../../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, updateTrashedDocsStatus } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { Doc } from '../../../../../types';
import { useState } from 'react';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import Typography from '@mui/material/Typography';

interface DriveRestoreDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedDocs: Doc[];
  driveId: string;
  selectedFolder: string;
  changeLogRoles: string[];
  isAllUsers?: boolean;
}

export default function DriveRestoreDialog({
  open,
  handleClose,
  userEmail,
  selectedDocs,
  driveId,
  selectedFolder,
  changeLogRoles,
  isAllUsers,
}: DriveRestoreDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      updateTrashedDocsStatus(userEmail, selectedDocs, false, changeLogMessage),
    onSuccess: () => {
      if (selectedDocs.length > 25) {
        setSnackbar({
          open: true,
          severity: 'info',
          message: 'The task to restore the documents or folders has started.',
        });
      } else {
        setSnackbar({
          open: true,
          severity: 'success',
          message:
            'The documents or folders were restored from trash successfully!',
        });

        // update all folders
        queryClient.invalidateQueries({ queryKey: [UserKeys.DriveFolders] });

        // update docs of current folder
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveDocuments, userEmail, selectedFolder],
        });

        // update docs of trash folder
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveDocuments, userEmail, `${driveId}.trash`],
        });
      }
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue restoring the selected documents or folders from trash. Please refresh and try again.',
      });
    },
    onSettled: () => {
      if (isAllUsers) {
        createEvent(analytics.drive.drive_explorer.restore_drive_content, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        });
      } else {
        createEvent(
          analytics.user_management.user_details.drive.restore_drive_content,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanRestoreDocs.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Restore from Trash"
      actingAs={userEmail}
      dialogContent={
        <>
          <Typography>
            {selectedDocs.length === 1
              ? `Do you wish to restore the selected ${
                  selectedDocs[0].mimeType.includes('folder')
                    ? 'folder'
                    : 'document'
                }: "${selectedDocs[0].title}"  from the trash?`
              : `Do you wish to restore the ${selectedDocs.length} selected documents or folders from the trash?`}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: 'Restore',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
